import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "../styles/ApplicantScoring.css";
import Header from '../components/Header';
import tuyenDungService from "../services/tuyenDungService";
import danhMucService from "../services/danhMucService";
import { transformApplicant } from "../utils/dataTransformers";
import { useNotification } from "../hooks/useNotification";
import Notification from "../components/Notification";

// Tách các mục kinh nghiệm xuống dòng theo mẫu ngày (DD/MM/YYYY hoặc YYYY - YYYY)
const formatExperienceLines = (text) => {
  if (!text) return text;
  return text.replace(/\s+(?=(?:\d{1,2}\/\d{1,2}\/\d{4}|\d{4})\s*[-–]\s*(?:\d{1,2}\/\d{1,2}\/\d{4}|\d{4}))/g, '\n');
};

const ApplicantScoring = () => {
  const { campaignId, applicantId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const displayName = user?.hoVaTen || user?.name || user?.username || '';
  const { notification, showSuccess, showError, showWarning, hideNotification } = useNotification();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applicant, setApplicant] = useState(null);
  const [criteriaList, setCriteriaList] = useState([]);
  const [ketQuaPVList, setKetQuaPVList] = useState([]);
  const [showApplicantInfo, setShowApplicantInfo] = useState(false);
  const [imageViewer, setImageViewer] = useState(null);

  useEffect(() => {
    fetchApplicant();
    fetchCriteria();
    fetchKetQuaPV();
  }, [applicantId]);

  const fetchApplicant = async () => {
    try {
      setLoading(true);

      const results = await Promise.allSettled([
        tuyenDungService.getById(applicantId),
        tuyenDungService.getBangCap(applicantId),
        tuyenDungService.getChungChi(applicantId),
        danhMucService.getTonGiao(),
        danhMucService.getTinhTrangHonNhan(),
        danhMucService.getTrangThaiTuyenDung(),
        danhMucService.getXepLoaiDaoTao()
      ]);

      const [applicantDataRes, bangCapsRes, chungChisRes, tonGiaosRes, tinhTrangHonNhansRes, trangThaiTuyenDungsRes, xepLoaiDaoTaosRes] = results;

      if (applicantDataRes.status !== 'fulfilled') {
        throw applicantDataRes.reason;
      }

      const tonGiaos = tonGiaosRes.status === 'fulfilled' ? tonGiaosRes.value : [];
      const tinhTrangHonNhans = tinhTrangHonNhansRes.status === 'fulfilled' ? tinhTrangHonNhansRes.value : [];
      const trangThaiTuyenDungs = trangThaiTuyenDungsRes.status === 'fulfilled' ? trangThaiTuyenDungsRes.value : [];
      const xepLoaiDaoTaos = xepLoaiDaoTaosRes.status === 'fulfilled' ? xepLoaiDaoTaosRes.value : [];

      if (tonGiaosRes.status === 'rejected' || tinhTrangHonNhansRes.status === 'rejected' || trangThaiTuyenDungsRes.status === 'rejected' || xepLoaiDaoTaosRes.status === 'rejected') {
        console.warn('Lookup data fetch failed, using empty lists.');
      }

      const lookupData = { tonGiaos, tinhTrangHonNhans, trangThaiTuyenDungs, xepLoaiDaoTaos };

      const applicantData = applicantDataRes.value;
      const bangCaps = bangCapsRes.status === 'fulfilled' ? bangCapsRes.value : [];
      const chungChis = chungChisRes.status === 'fulfilled' ? chungChisRes.value : [];

      console.log('Raw applicant data:', applicantData);
      console.log('BangCaps from API:', bangCaps);
      console.log('ChungChis from API:', chungChis);

      // Gắn bangCaps và chungChis vào applicantData
      applicantData.bangCaps = bangCaps;
      applicantData.chungChis = chungChis;

      const transformedData = transformApplicant(applicantData, lookupData);
      console.log('Transformed certificates:', transformedData.certificates);
      console.log('Transformed degrees:', transformedData.degrees);
      setApplicant(transformedData);
      setError(null);
    } catch (err) {
      console.error('Error fetching applicant:', err);
      setError('Không thể tải dữ liệu. Vui lòng thử lại sau.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCriteria = async () => {
    try {
      const response = await danhMucService.getTieuChiPV();
      console.log('Tieu chi PV response:', response);

 
      const criteriaData = response?.data || response;

      if (criteriaData && Array.isArray(criteriaData) && criteriaData.length > 0) {
 
        const dynamicScores = {};
        criteriaData.forEach((criterion) => {
          const key = `criterion_${criterion.id}`;
          dynamicScores[key] = {
            score: 0,
            maxScore: criterion.thangDiem,
            label: criterion.tenTieuChiPV,
            description: `Thang điểm: ${criterion.thangDiem} điểm`,
            stt: criterion.stt,
            id: criterion.id
          };
        });
        console.log('Dynamic scores:', dynamicScores);
        setCriteriaList(criteriaData);
        setScores(dynamicScores);


        await loadExistingScoreWithCriteria(dynamicScores);
      } else {
        console.warn('No criteria data found:', criteriaData);
      }
    } catch (err) {
      console.error('Error fetching criteria:', err);

    }
  };

  const loadExistingScoreWithCriteria = async (criteriaScores) => {
    try {
      const maSo = user?.maSo || user?.username;
      if (!maSo || !applicantId || !campaignId) {
        console.log('Missing required data:', { maSo, applicantId, campaignId });
        return;
      }

      console.log('Loading existing score for:', { applicantId, campaignId, maSo });

 
      const allResults = await tuyenDungService.getKetQuaChamDiem(parseInt(applicantId), parseInt(campaignId));
      console.log('All results from API:', allResults);

 
      const myResult = allResults.find(result => result.maSo === maSo);
      console.log('My result:', myResult);

      if (myResult) {
   
        let chiTietChamDiem = [];
        if (typeof myResult.ketQua === 'string') {
          try {
            chiTietChamDiem = JSON.parse(myResult.ketQua);
            console.log('Parsed chiTietChamDiem:', chiTietChamDiem);
          } catch (parseErr) {
            console.error('Error parsing ketQua:', parseErr);
            return;
          }
        } else if (Array.isArray(myResult.ketQua)) {
          chiTietChamDiem = myResult.ketQua;
        }

        if (chiTietChamDiem && chiTietChamDiem.length > 0) {

          const updatedScores = { ...criteriaScores };
          chiTietChamDiem.forEach(item => {
            const key = `criterion_${item.idTieuChiPV}`;
            console.log('Loading score for criterion:', key, item.diem);
            if (updatedScores[key]) {
              updatedScores[key].score = item.diem;
            }
          });

          setScores(updatedScores);
          console.log('Updated scores:', updatedScores);

     
          if (myResult.ghiChu) {
            console.log('Setting notes:', myResult.ghiChu);
            setNotes(myResult.ghiChu);
          }
          if (myResult.ketQuaPV && myResult.ketQuaPV.id) {
            console.log('Setting recommendation:', myResult.ketQuaPV.id);
            setRecommendation(myResult.ketQuaPV.id.toString());
          }

          console.log('Successfully loaded existing score');
        }
      } else {
        console.log('No existing score found for this MaSo');
      }
    } catch (err) {
      console.error('Error loading existing score:', err);

    }
  };

  const fetchKetQuaPV = async () => {
    try {
      const response = await danhMucService.getKetQuaPV();
      console.log('Ket qua PV response:', response);


      const ketQuaData = response?.data || response;

      if (ketQuaData && Array.isArray(ketQuaData) && ketQuaData.length > 0) {
        setKetQuaPVList(ketQuaData);
      } else {
        console.warn('No ket qua PV data found:', ketQuaData);
      }
    } catch (err) {
      console.error('Error fetching ket qua PV:', err);
    }
  };

  const [scores, setScores] = useState({});

  const [notes, setNotes] = useState("");
  const [recommendation, setRecommendation] = useState("");

  const totalScore = Object.values(scores).reduce(
    (sum, item) => sum + item.score,
    0
  );
  const maxTotalScore = Object.values(scores).reduce(
    (sum, item) => sum + item.maxScore,
    0
  );
  const percentage = Math.round((totalScore / maxTotalScore) * 100);

  const handleScoreChange = (key, value) => {
    const numValue = parseInt(value) || 0;
    const maxScore = scores[key].maxScore;
    const finalValue = Math.min(Math.max(0, numValue), maxScore);

    setScores((prev) => ({
      ...prev,
      [key]: { ...prev[key], score: finalValue },
    }));
  };

  const getScoreColor = (percent) => {
    if (percent >= 80) return "excellent";
    if (percent >= 60) return "good";
    if (percent >= 40) return "average";
    return "poor";
  };

  const getRecommendationIcon = (tenKetQua) => {
    const normalizedName = tenKetQua.toLowerCase();


    if (normalizedName.includes('đề xuất') || normalizedName.includes('tuyển')) {
      return (
        <>
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </>
      );
    }


    if (normalizedName.includes('cân nhắc')) {
      return (
        <>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </>
      );
    }

    if (normalizedName.includes('không đạt')) {
      return (
        <>
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </>
      );
    }

    if (normalizedName.includes('không phỏng vấn')) {
      return (
        <>
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="8.5" cy="7" r="4" />
          <line x1="23" y1="1" x2="1" y2="23" />
        </>
      );
    }

    return (
      <>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </>
    );
  };

  const getRecommendationClass = (tenKetQua) => {
    const normalizedName = tenKetQua.toLowerCase();

    if (normalizedName.includes('đề xuất') || normalizedName.includes('tuyển')) {
      return 'approve';
    }
    if (normalizedName.includes('cân nhắc')) {
      return 'consider';
    }
    return 'reject';
  };

  const handleSave = () => {
    const data = {
      applicantId,
      campaignId,
      scores,
      totalScore,
      percentage,
      notes,
      recommendation,
      scoredBy: displayName,
      scoredAt: new Date().toISOString(),
    };
    console.log("Saving score:", data);
    showSuccess("Đã lưu đánh giá thành công!");
  };

 const handleSubmit = async () => {
  if (!recommendation) {
    showWarning("Vui lòng chọn đề xuất kết quả!");
    return;
  }

  try {
    const ketQuaList = Object.entries(scores).map(([key, item]) => ({
      idTieuChiPV: item.id,
      tenTieuChiPV: item.label,
      thangDiem: item.maxScore,
      diem: item.score
    }));

    const payload = {
      idDotTuyenDung: parseInt(campaignId),
      idTuyenDung: parseInt(applicantId),
      idKetQuaPV: parseInt(recommendation),
      maSo: user?.maSo || user?.username,
      hoVaTenNguoiCham: user?.hoVaTen || user?.name || user?.username,
      diemSo: totalScore,
      ghiChu: notes,
      ketQua: ketQuaList
    };

    await tuyenDungService.chamDiem(payload);
    showSuccess("Đã lưu kết quả chấm điểm thành công!");
    setTimeout(() => {
      navigate(`/campaign/${campaignId}`, { replace: true, state: { refresh: Date.now() } });
    }, 1000);
  } catch (error) {
    console.error("Lỗi khi gửi đánh giá:", error);
    showError("Có lỗi xảy ra khi lưu đánh giá. Vui lòng thử lại!");
  }
};


  if (loading) {
    return (
      <div className="scoring-wrapper">
        <div className="scoring-container">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <p>Đang tải dữ liệu...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !applicant) {
    return (
      <div className="scoring-wrapper">
        <div className="scoring-container">
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: '1rem' }}>
            <p style={{ color: '#dc2626' }}>{error || 'Không tìm thấy ứng viên'}</p>
            <button onClick={() => navigate(`/campaign/${campaignId}`)} className="btn btn-primary">Quay lại</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="scoring-wrapper">
      <div className="app-background">
        <div className="floating-icons">
          <div className="floating-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z" />
            </svg>
          </div>
          <div className="floating-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          </div>
        </div>
      </div>

      <div className="scoring-container">
        <Header title="Chấm điểm ứng viên" />

        <div className="scoring-breadcrumb">
          <button
            onClick={() => navigate("/trangchu")}
            className="breadcrumb-link"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              width="18"
              height="18"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            Trang Chủ
          </button>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            width="16"
            height="16"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
          <button
            onClick={() => navigate(`/campaign/${campaignId}`)}
            className="breadcrumb-link"
          >
            Đợt tuyển dụng
          </button>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            width="16"
            height="16"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
          <span className="breadcrumb-current">{applicant.fullName}</span>
        </div>

        {/* Mobile compact applicant info card */}
        <div className="applicant-compact-card mobile-only" onClick={() => setShowApplicantInfo(true)}>
          <div className="compact-avatar">
            {applicant.avatar ? (
              <img src={applicant.avatar} alt={applicant.fullName} />
            ) : (
              <span>{applicant.fullName.charAt(0)}</span>
            )}
          </div>
          <div className="compact-info">
            <h3>{applicant.fullName}</h3>
            <p>{applicant.position}</p>
            <span className="compact-meta">
              {applicant.phone} • {applicant.email}
            </span>
          </div>
          <div className="compact-action">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </div>
        </div>

        <main className="scoring-main">
          <div className="cv-panel">
            <div className="panel-header">
              <h2>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  width="22"
                  height="22"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
                Hồ sơ ứng viên
              </h2>
              <span className="position-tag">{applicant.position}</span>
            </div>

            <div className="cv-content">
              <section className="cv-section">
                <h3 className="section-heading">
                  <span className="section-number">I</span>
                  Thông tin cá nhân
                </h3>

                <div className="cv-avatar-section">
                  <div className="cv-avatar">
                    {applicant.avatar ? (
                      <img src={applicant.avatar} alt={applicant.fullName} />
                    ) : (
                      <span>{applicant.fullName.charAt(0)}</span>
                    )}
                  </div>
                  <div className="cv-basic-info">
                    <h4>{applicant.fullName}</h4>
                    <p>{applicant.position}</p>
                    <span className="applied-date">
                      Nộp hồ sơ: {applicant.appliedDate}
                    </span>
                  </div>
                </div>

                <div className="info-grid">
                  <div className="info-item">
                    <label>2. Họ và tên:</label>
                    <span>{applicant.fullName}</span>
                  </div>
                  <div className="info-item">
                    <label>3. Ngày tháng năm sinh:</label>
                    <span>{applicant.birthDate}</span>
                  </div>
                  <div className="info-item">
                    <label>4. Giới tính:</label>
                    <span>{applicant.gender}</span>
                  </div>
                  <div className="info-item">
                    <label>5. Số CCCD:</label>
                    <span>{applicant.cccd}</span>
                  </div>
                  <div className="info-item">
                    <label>6. Ngày cấp CCCD:</label>
                    <span>{applicant.cccdDate}</span>
                  </div>
                  <div className="info-item full-width">
                    <label>7. Nơi cấp CCCD:</label>
                    <span>{applicant.cccdPlace}</span>
                  </div>
                  <div className="info-item full-width">
                    <label>8. Địa chỉ thường trú:</label>
                    <span>{applicant.permanentAddress}</span>
                  </div>
                  <div className="info-item full-width">
                    <label>9. Chỗ ở hiện tại:</label>
                    <span>{applicant.currentAddress}</span>
                  </div>
                  <div className="info-item">
                    <label>10. Quê quán:</label>
                    <span>{applicant.hometown}</span>
                  </div>
                  <div className="info-item">
                    <label>11. Nơi sinh:</label>
                    <span>{applicant.birthPlace}</span>
                  </div>
                  <div className="info-item">
                    <label>12. Dân tộc:</label>
                    <span>{applicant.ethnicity}</span>
                  </div>
                  <div className="info-item">
                    <label>13. Tôn giáo:</label>
                    <span>{applicant.religion}</span>
                  </div>
                  <div className="info-item">
                    <label>14. Tình trạng hôn nhân:</label>
                    <span>{applicant.maritalStatus}</span>
                  </div>
                  <div className="info-item">
                    <label>15. Số con (nếu có):</label>
                    <span>{applicant.numberOfChildren}</span>
                  </div>
                  <div className="info-item">
                    <label>16. Tuổi con nhỏ nhất:</label>
                    <span>{applicant.youngestChildAge} tuổi</span>
                  </div>
                  <div className="info-item">
                    <label>17. Số điện thoại:</label>
                    <span className="highlight-text">{applicant.phone}</span>
                  </div>
                  <div className="info-item">
                    <label>18. Email:</label>
                    <span className="highlight-text">{applicant.email}</span>
                  </div>
                </div>

                <div className="info-subsection">
                  <h4>19. Thông tin người liên hệ khi cần thiết:</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Họ và tên:</label>
                      <span>{applicant.emergencyContact.name}</span>
                    </div>
                    <div className="info-item">
                      <label>Số điện thoại:</label>
                      <span>{applicant.emergencyContact.phone}</span>
                    </div>
                    <div className="info-item">
                      <label>Mối quan hệ:</label>
                      <span>{applicant.emergencyContact.relationship}</span>
                    </div>
                  </div>
                </div>

                <div className="info-subsection">
                  <h4>20. Kinh nghiệm &amp; Kỹ năng:</h4>
                  <div className="info-grid">
                    <div className="info-item full-width">
                      <label>Kinh nghiệm làm việc:</label>
                      <span className="experience-multiline">{formatExperienceLines(applicant.experience) || 'Chưa có'}</span>
                    </div>
                    <div className="info-item full-width">
                      <label>Kỹ năng chuyên môn:</label>
                      <span className="experience-multiline">{applicant.skills || 'Chưa có'}</span>
                    </div>
                    {applicant.softSkills && (
                      <div className="info-item full-width">
                        <label>Kỹ năng mềm / Sở thích:</label>
                        <span className="experience-multiline">{applicant.softSkills}</span>
                      </div>
                    )}
                    {applicant.careerExpectation && (
                      <div className="info-item full-width">
                        <label>Nguyện vọng:</label>
                        <span className="experience-multiline">{applicant.careerExpectation}</span>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              <section className="cv-section">
                <h3 className="section-heading">
                  <span className="section-number">II</span>
                  Thông tin sức khỏe
                </h3>

                <div className="info-grid">
                  <div className="info-item">
                    <label>1. Tình trạng sức khỏe hiện tại:</label>
                    <span className="health-status good">
                      {applicant.healthStatus}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>2. Chiều cao (cm):</label>
                    <span>{applicant.height} cm</span>
                  </div>
                  <div className="info-item">
                    <label>3. Cân nặng (kg):</label>
                    <span>{applicant.weight} kg</span>
                  </div>
                  <div className="info-item full-width">
                    <label>4. Tiền sử bệnh/Bệnh lý cần lưu ý:</label>
                    <span>{applicant.medicalHistory}</span>
                  </div>
                </div>
              </section>

              <section className="cv-section">
                <h3 className="section-heading">
                  <span className="section-number">III</span>
                  Trình độ chuyên môn – Chứng chỉ
                </h3>

                <div className="degrees-section">
                  <h4>1. Bằng cấp:</h4>
                  <div className="degrees-list">
                    {applicant.degrees.map((degree, index) => (
                      <div
                        key={index}
                        className={`degree-card ${degree.image ? 'clickable' : ''}`}
                        onClick={() => degree.image && setImageViewer({ url: degree.image, title: degree.name })}
                        title={degree.image ? 'Click để xem ảnh bằng cấp' : ''}
                      >
                        <div className="degree-icon">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                            <path d="M6 12v5c3 3 9 3 12 0v-5" />
                          </svg>
                        </div>
                        <div className="degree-info">
                          <h5>{degree.name}</h5>
                          <p>{degree.school}</p>
                          <div className="degree-meta">
                            <span>Năm tốt nghiệp: {degree.year}</span>
                            <span className="degree-grade">
                              Xếp loại: {degree.grade}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {(applicant.licenseNumber || applicant.licenseIssueDate || applicant.licensePlace || applicant.licenseDegree || applicant.licenseScope) && (
                  <div className="degrees-section">
                    <h4>2. Giấy phép hành nghề (GPHN):</h4>
                    <div className="info-grid">
                      {applicant.licenseNumber && (
                        <div className="info-item">
                          <label>Số GPHN:</label>
                          <span>{applicant.licenseNumber}</span>
                        </div>
                      )}
                      {applicant.licenseIssueDate && (
                        <div className="info-item">
                          <label>Ngày cấp:</label>
                          <span>{applicant.licenseIssueDate}</span>
                        </div>
                      )}
                      {applicant.licensePlace && (
                        <div className="info-item full-width">
                          <label>Nơi cấp:</label>
                          <span>{applicant.licensePlace}</span>
                        </div>
                      )}
                      {applicant.licenseDegree && (
                        <div className="info-item full-width">
                          <label>Văn bằng chuyên môn:</label>
                          <span>{applicant.licenseDegree}</span>
                        </div>
                      )}
                      {applicant.licenseScope && (
                        <div className="info-item full-width">
                          <label>Phạm vi hoạt động:</label>
                          <span>{applicant.licenseScope}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="certificates-section">
                  <h4>3. Chứng chỉ:</h4>
                  <div className="certificates-list">
                    {applicant.certificates.map((cert, index) => {
                      const certName = typeof cert === 'string' ? cert : cert.name;
                      const certImage = typeof cert === 'object' && cert.image ? cert.image : null;

                      return (
                        <div
                          key={index}
                          className={`certificate-item ${certImage ? 'clickable' : ''}`}
                          onClick={() => certImage && setImageViewer({ url: certImage, title: certName })}
                          title={certImage ? 'Click để xem ảnh chứng chỉ' : ''}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                          </svg>
                          <span>{certName}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
              <footer className="page-footerr">
                <p>&copy; Copyright 2026 {process.env.REACT_APP_FOOTER_NAME || 'Bệnh Viện Hùng Vương Gia Lai'}</p>
                <p>Powered by Ban Chuyển Đổi Số - leanhquan™</p>
              </footer>
            </div>
          </div>

          <div className="scoring-panel">
            <div className="panel-header">
              <h2>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  width="22"
                  height="22"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Chấm điểm đánh giá
              </h2>
            </div>

            <div className="scoring-content">
              <div className="total-score-card">
                <div className={`score-circle ${getScoreColor(percentage)}`}>
                  <div className="score-value">
                    <span className="score-number">{totalScore}</span>
                    <span className="score-max">/{maxTotalScore}</span>
                  </div>
                  <div className="score-percent">{percentage}%</div>
                </div>
                <div className="score-legend">
                  <div className="legend-item excellent">
                    <span className="dot"></span>Xuất sắc (≥80%)
                  </div>
                  <div className="legend-item good">
                    <span className="dot"></span>Tốt (60-79%)
                  </div>
                  <div className="legend-item average">
                    <span className="dot"></span>Trung bình (40-59%)
                  </div>
                  <div className="legend-item poor">
                    <span className="dot"></span>Yếu (&lt;40%)
                  </div>
                </div>
              </div>

              <div className="criteria-section">
                <h3>Tiêu chí đánh giá</h3>
                <div className="criteria-list">
                  {Object.entries(scores).map(([key, item]) => {
                    const itemPercent = Math.round(
                      (item.score / item.maxScore) * 100
                    );
                    return (
                      <div key={key} className="criteria-item">
                        <div className="criteria-header">
                          <div className="criteria-info">
                            <h4>{item.label}</h4>
                            <p>{item.description}</p>
                          </div>
                          <div className="criteria-score">
                            <input
                              type="number"
                              min="0"
                              max={item.maxScore}
                              value={item.score}
                              onChange={(e) =>
                                handleScoreChange(key, e.target.value)
                              }
                            />
                            <span className="max-score">/ {item.maxScore}</span>
                          </div>
                        </div>
                        <div className="criteria-progress">
                          <div
                            className={`progress-bar ${getScoreColor(
                              itemPercent
                            )}`}
                            style={{ width: `${itemPercent}%` }}
                          ></div>
                        </div>
                        <div className="criteria-quick-scores">
                          {Array.from(
                            { length: item.maxScore + 1 },
                            (_, val) => (
                              <button
                                key={val}
                                className={`quick-score-btn ${item.score === val ? "active" : ""
                                  }`}
                                onClick={() => handleScoreChange(key, val)}
                              >
                                {val}
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="notes-section">
                <h3>Ghi chú đánh giá</h3>
                <textarea
                  className="notes-textarea"
                  placeholder="Nhập ghi chú, nhận xét về ứng viên..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                />
              </div>

              <div className="recommendation-section">
                <h3>Đề xuất kết quả</h3>
                <div className="recommendation-options">
                  {ketQuaPVList.map((ketQua) => (
                    <label
                      key={ketQua.id}
                      className={`recommendation-option ${getRecommendationClass(ketQua.tenKetQuaPV)} ${
                        recommendation === ketQua.id.toString() ? "selected" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="recommendation"
                        value={ketQua.id.toString()}
                        checked={recommendation === ketQua.id.toString()}
                        onChange={(e) => setRecommendation(e.target.value)}
                      />
                      <div className="option-content">
                        <div className="option-icon">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            {getRecommendationIcon(ketQua.tenKetQuaPV)}
                          </svg>
                        </div>
                        <div className="option-text">
                          <span className="option-title">{ketQua.tenKetQuaPV}</span>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

            </div>

            <div className="scoring-actions">
              <button
                className="btn btn-outline"
                onClick={() => navigate(`/campaign/${campaignId}`)}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  width="18"
                  height="18"
                >
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
                Quay lại
              </button>
              <div className="action-group">
                <button className="btn btn-primary" onClick={handleSubmit}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    width="18"
                    height="18"
                  >
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                  Gửi đánh giá
                </button>
              </div>
            </div>
          </div>
        </main>

        <footer className="scoring-footer">
          <p>&copy; Copyright 2026 {process.env.REACT_APP_FOOTER_NAME || 'Bệnh Viện Hùng Vương Gia Lai'}</p>
          <p>Powered by Phòng Công Nghệ Thông Tin - leanhquan™</p>
        </footer>
      </div>

      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
          duration={notification.duration}
        />
      )}

      {/* Mobile applicant info modal */}
      {showApplicantInfo && (
        <div className="modal-overlay" onClick={() => setShowApplicantInfo(false)}>
          <div className="modal-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="modal-handle"></div>
            <div className="modal-header">
              <h2>Hồ sơ ứng viên</h2>
              <button className="modal-close" onClick={() => setShowApplicantInfo(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="modal-content">
              <section className="cv-section">
                <h3 className="section-heading">
                  <span className="section-number">I</span>
                  Thông tin cá nhân
                </h3>

                <div className="cv-avatar-section">
                  <div className="cv-avatar">
                    {applicant.avatar ? (
                      <img src={applicant.avatar} alt={applicant.fullName} />
                    ) : (
                      <span>{applicant.fullName.charAt(0)}</span>
                    )}
                  </div>
                  <div className="cv-basic-info">
                    <h4>{applicant.fullName}</h4>
                    <p>{applicant.position}</p>
                    <span className="applied-date">
                      Nộp hồ sơ: {applicant.appliedDate}
                    </span>
                  </div>
                </div>

                <div className="info-grid">
                  <div className="info-item">
                    <label>2. Họ và tên:</label>
                    <span>{applicant.fullName}</span>
                  </div>
                  <div className="info-item">
                    <label>3. Ngày tháng năm sinh:</label>
                    <span>{applicant.birthDate}</span>
                  </div>
                  <div className="info-item">
                    <label>4. Giới tính:</label>
                    <span>{applicant.gender}</span>
                  </div>
                  <div className="info-item">
                    <label>5. Số CCCD:</label>
                    <span>{applicant.cccd}</span>
                  </div>
                  <div className="info-item">
                    <label>6. Ngày cấp CCCD:</label>
                    <span>{applicant.cccdDate}</span>
                  </div>
                  <div className="info-item full-width">
                    <label>7. Nơi cấp CCCD:</label>
                    <span>{applicant.cccdPlace}</span>
                  </div>
                  <div className="info-item full-width">
                    <label>8. Địa chỉ thường trú:</label>
                    <span>{applicant.permanentAddress}</span>
                  </div>
                  <div className="info-item full-width">
                    <label>9. Chỗ ở hiện tại:</label>
                    <span>{applicant.currentAddress}</span>
                  </div>
                  <div className="info-item">
                    <label>10. Quê quán:</label>
                    <span>{applicant.hometown}</span>
                  </div>
                  <div className="info-item">
                    <label>11. Nơi sinh:</label>
                    <span>{applicant.birthPlace}</span>
                  </div>
                  <div className="info-item">
                    <label>12. Dân tộc:</label>
                    <span>{applicant.ethnicity}</span>
                  </div>
                  <div className="info-item">
                    <label>13. Tôn giáo:</label>
                    <span>{applicant.religion}</span>
                  </div>
                  <div className="info-item">
                    <label>14. Tình trạng hôn nhân:</label>
                    <span>{applicant.maritalStatus}</span>
                  </div>
                  <div className="info-item">
                    <label>15. Số con (nếu có):</label>
                    <span>{applicant.numberOfChildren}</span>
                  </div>
                  <div className="info-item">
                    <label>16. Tuổi con nhỏ nhất:</label>
                    <span>{applicant.youngestChildAge} tuổi</span>
                  </div>
                  <div className="info-item">
                    <label>17. Số điện thoại:</label>
                    <span className="highlight-text">{applicant.phone}</span>
                  </div>
                  <div className="info-item">
                    <label>18. Email:</label>
                    <span className="highlight-text">{applicant.email}</span>
                  </div>
                </div>

                <div className="info-subsection">
                  <h4>19. Thông tin người liên hệ khi cần thiết:</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Họ và tên:</label>
                      <span>{applicant.emergencyContact.name}</span>
                    </div>
                    <div className="info-item">
                      <label>Số điện thoại:</label>
                      <span>{applicant.emergencyContact.phone}</span>
                    </div>
                    <div className="info-item">
                      <label>Mối quan hệ:</label>
                      <span>{applicant.emergencyContact.relationship}</span>
                    </div>
                  </div>
                </div>

                <div className="info-subsection">
                  <h4>20. Kinh nghiệm &amp; Kỹ năng:</h4>
                  <div className="info-grid">
                    <div className="info-item full-width">
                      <label>Kinh nghiệm làm việc:</label>
                      <span className="experience-multiline">{formatExperienceLines(applicant.experience) || 'Chưa có'}</span>
                    </div>
                    <div className="info-item full-width">
                      <label>Kỹ năng chuyên môn:</label>
                      <span className="experience-multiline">{applicant.skills || 'Chưa có'}</span>
                    </div>
                    {applicant.softSkills && (
                      <div className="info-item full-width">
                        <label>Kỹ năng mềm / Sở thích:</label>
                        <span className="experience-multiline">{applicant.softSkills}</span>
                      </div>
                    )}
                    {applicant.careerExpectation && (
                      <div className="info-item full-width">
                        <label>Nguyện vọng:</label>
                        <span className="experience-multiline">{applicant.careerExpectation}</span>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              <section className="cv-section">
                <h3 className="section-heading">
                  <span className="section-number">II</span>
                  Thông tin sức khỏe
                </h3>

                <div className="info-grid">
                  <div className="info-item">
                    <label>1. Tình trạng sức khỏe hiện tại:</label>
                    <span className="health-status good">
                      {applicant.healthStatus}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>2. Chiều cao (cm):</label>
                    <span>{applicant.height} cm</span>
                  </div>
                  <div className="info-item">
                    <label>3. Cân nặng (kg):</label>
                    <span>{applicant.weight} kg</span>
                  </div>
                  <div className="info-item full-width">
                    <label>4. Tiền sử bệnh/Bệnh lý cần lưu ý:</label>
                    <span>{applicant.medicalHistory}</span>
                  </div>
                </div>
              </section>

              <section className="cv-section">
                <h3 className="section-heading">
                  <span className="section-number">III</span>
                  Trình độ chuyên môn – Chứng chỉ
                </h3>

                <div className="degrees-section">
                  <h4>1. Bằng cấp:</h4>
                  <div className="degrees-list">
                    {applicant.degrees.map((degree, index) => (
                      <div
                        key={index}
                        className={`degree-card ${degree.image ? 'clickable' : ''}`}
                        onClick={() => degree.image && setImageViewer({ url: degree.image, title: degree.name })}
                        title={degree.image ? 'Click để xem ảnh bằng cấp' : ''}
                      >
                        <div className="degree-icon">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                            <path d="M6 12v5c3 3 9 3 12 0v-5" />
                          </svg>
                        </div>
                        <div className="degree-info">
                          <h5>{degree.name}</h5>
                          <p>{degree.school}</p>
                          <div className="degree-meta">
                            <span>Năm tốt nghiệp: {degree.year}</span>
                            <span className="degree-grade">
                              Xếp loại: {degree.grade}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {(applicant.licenseNumber || applicant.licenseIssueDate || applicant.licensePlace || applicant.licenseDegree || applicant.licenseScope) && (
                  <div className="degrees-section">
                    <h4>2. Giấy phép hành nghề (GPHN):</h4>
                    <div className="info-grid">
                      {applicant.licenseNumber && (
                        <div className="info-item">
                          <label>Số GPHN:</label>
                          <span>{applicant.licenseNumber}</span>
                        </div>
                      )}
                      {applicant.licenseIssueDate && (
                        <div className="info-item">
                          <label>Ngày cấp:</label>
                          <span>{applicant.licenseIssueDate}</span>
                        </div>
                      )}
                      {applicant.licensePlace && (
                        <div className="info-item full-width">
                          <label>Nơi cấp:</label>
                          <span>{applicant.licensePlace}</span>
                        </div>
                      )}
                      {applicant.licenseDegree && (
                        <div className="info-item full-width">
                          <label>Văn bằng chuyên môn:</label>
                          <span>{applicant.licenseDegree}</span>
                        </div>
                      )}
                      {applicant.licenseScope && (
                        <div className="info-item full-width">
                          <label>Phạm vi hoạt động:</label>
                          <span>{applicant.licenseScope}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="certificates-section">
                  <h4>3. Chứng chỉ:</h4>
                  <div className="certificates-list">
                    {applicant.certificates.map((cert, index) => {
                      const certName = typeof cert === 'string' ? cert : cert.name;
                      const certImage = typeof cert === 'object' && cert.image ? cert.image : null;

                      return (
                        <div
                          key={index}
                          className={`certificate-item ${certImage ? 'clickable' : ''}`}
                          onClick={() => certImage && setImageViewer({ url: certImage, title: certName })}
                          title={certImage ? 'Click để xem ảnh chứng chỉ' : ''}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                          </svg>
                          <span>{certName}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
              <footer className="page-footerr">
                <p>&copy; Copyright 2026 {process.env.REACT_APP_FOOTER_NAME || 'Bệnh Viện Hùng Vương Gia Lai'}</p>
                <p>Powered by Phòng Công Nghệ Thông Tin - leanhquan™</p>
              </footer>
            </div>
          </div>
        </div>
      )}

      {imageViewer && (
        <div className="image-viewer-overlay" onClick={() => setImageViewer(null)}>
          <div className="image-viewer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="image-viewer-header">
              <div className="image-viewer-title">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                <h4>{imageViewer.title}</h4>
              </div>
              <div className="image-viewer-actions">
                <a
                  href={imageViewer.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-open-new-tab"
                  title="Mở tab mới"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </a>
                <button className="image-viewer-close" onClick={() => setImageViewer(null)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="image-viewer-body">
              {imageViewer.url.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={imageViewer.url}
                  title={imageViewer.title}
                  className="pdf-viewer"
                />
              ) : (
                <img src={imageViewer.url} alt={imageViewer.title} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicantScoring;

